// ============================================================
// CssQuiz.js
//
// Gamified CSS & SCSS Mastery Quiz — ProDev Platform
//
// Adapted from the core ProDev Quiz Engine.
// Brand new CSS/UI question bank covering:
//   CSS Fundamentals, Flexbox, CSS Grid, SCSS Preprocessor,
//   Animations & Transitions, Responsive Design & BEM.
//
// Features:
//   - Personalised welcome screen (greets user by name)
//   - Rules & conduct screen with time limits explained
//   - Web Speech API — audible countdown before quiz starts
//   - Dynamic difficulty: re-arranges easy → hard on retry
//     based on initial performance per category
//   - Star reward system (★★★ = 100% Golden Buzzer)
//   - Anti-cheat engine (Tab switching, Right-click, Shortcuts blocked)
//   - Sound synthesis (Web Audio API)
//   - Category-level performance breakdown
//   - Leaderboard (localStorage — isolated to CSS)
//
// Junior dev notes throughout to explain architecture.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Card, CardContent, Typography,
    LinearProgress, Chip, Divider,
    styled, keyframes,
} from '@mui/material';
import { Link } from 'react-router-dom';

// ── MUI Icons ─────────────────────────────────────────────────
import StarIcon               from '@mui/icons-material/Star';
import StarBorderIcon         from '@mui/icons-material/StarBorder';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import TimerIcon              from '@mui/icons-material/Timer';
import SchoolIcon             from '@mui/icons-material/School';
import SecurityIcon           from '@mui/icons-material/Security';
import PlayArrowIcon          from '@mui/icons-material/PlayArrow';
import ReplayIcon             from '@mui/icons-material/Replay';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

// ============================================================
//  BRAND CONSTANTS
// ============================================================
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    goldenrod:  '#DAA520',
    goldFaint:  'rgba(229,201,95,0.08)',
    white:      '#FFFFFF',
    dimWhite:   'rgba(255,255,255,0.65)',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
    warning:    '#FF9F43',
};

// ============================================================
//  QUIZ CONFIGURATION
// ============================================================
const QUIZ_CONFIG = {
    totalTimeSeconds: 900,    // 15 minutes for 18 questions
    warningThreshold: 60,
    passMark:         0.80,   // 80% = 1 star minimum
    twoStarMark:      0.90,   // 90% = 2 stars
    threeStarMark:    1.00,   // 100% = 3 stars + Golden Buzzer
    maxViolations:    3,
    leaderboardKey:   'css_quiz_leaderboard',
};

// ============================================================
//  CSS QUESTION BANK
// ============================================================
const cssQuestionBank = [

    // ── CSS Fundamentals ───────────────────────────────────────
    {
        question: 'Which CSS property and value ensures that padding and borders are included in the element\'s total width and height?',
        options: ['box-sizing: content-box;', 'box-sizing: border-box;', 'display: block;', 'width: auto;'],
        answer: 'box-sizing: border-box;',
        explanation: '`box-sizing: border-box;` is the universal fix to ensure padding pushes inward rather than expanding the layout outward.',
        link: '/css-fundamentals',
        category: 'CSS Fundamentals',
        difficulty: 'easy',
    },
    {
        question: 'Which of the following selectors has the highest specificity score?',
        options: ['.header-nav', '#main-content', 'header > nav', '!important (Added to a property)'],
        answer: '!important (Added to a property)',
        explanation: '`!important` overrides normal specificity rules. IDs beat classes, and classes beat elements, but `!important` beats them all (use it sparingly!).',
        link: '/css-fundamentals',
        category: 'CSS Fundamentals',
        difficulty: 'easy',
    },
    {
        question: 'What is the primary difference between the `rem` and `em` relative units?',
        options: [
            '`rem` is relative to the root html font-size; `em` is relative to the direct parent\'s font-size.',
            '`em` is relative to the root html font-size; `rem` is relative to the direct parent\'s font-size.',
            '`rem` is for margins only; `em` is for typography only.',
            'There is no difference; they are aliases for the same unit.',
        ],
        answer: '`rem` is relative to the root html font-size; `em` is relative to the direct parent\'s font-size.',
        explanation: '`rem` (Root EM) avoids the compounding sizing issues that occur when nesting elements that use `em` units.',
        link: '/css-fundamentals',
        category: 'CSS Fundamentals',
        difficulty: 'medium',
    },

    // ── Flexbox Deep Dive ────────────────────────────────
    {
        question: 'How do you change the Main Axis of a flex container from horizontal to vertical?',
        options: ['align-items: center;', 'justify-content: vertical;', 'flex-direction: column;', 'display: block;'],
        answer: 'flex-direction: column;',
        explanation: '`flex-direction: column;` rotates the Main Axis vertically, causing `justify-content` to handle vertical alignment instead of horizontal.',
        link: '/css-flexbox',
        category: 'Modern Layouts (Flexbox)',
        difficulty: 'easy',
    },
    {
        question: 'Which property is used to align flex items along the Cross Axis?',
        options: ['justify-content', 'align-items', 'flex-wrap', 'gap'],
        answer: 'align-items',
        explanation: '`justify-content` aligns items on the Main Axis, while `align-items` aligns them on the perpendicular Cross Axis.',
        link: '/css-flexbox',
        category: 'Modern Layouts (Flexbox)',
        difficulty: 'medium',
    },
    {
        question: 'In the shorthand `flex: 1 1 auto;`, what do the three values represent respectively?',
        options: [
            'flex-grow, flex-shrink, flex-basis',
            'flex-basis, flex-grow, flex-shrink',
            'flex-direction, flex-wrap, flex-flow',
            'margin, padding, width'
        ],
        answer: 'flex-grow, flex-shrink, flex-basis',
        explanation: 'This shorthand tells the item how much it can grow to fill space, how much it can shrink when space is tight, and its ideal starting size.',
        link: '/css-flexbox',
        category: 'Modern Layouts (Flexbox)',
        difficulty: 'hard',
    },

    // ── CSS Grid ──────────────────────────────────────────
    {
        question: 'How do you activate the CSS Grid context on a parent element?',
        options: ['grid-template: active;', 'display: grid;', 'position: grid;', 'float: grid;'],
        answer: 'display: grid;',
        explanation: 'Applying `display: grid;` to a container immediately turns all of its direct children into grid items.',
        link: '/css-grid',
        category: 'Advanced Layouts (CSS Grid)',
        difficulty: 'easy',
    },
    {
        question: 'What does the `fr` unit represent in CSS Grid?',
        options: [
            'Fixed Ratio (A set percentage of the screen)',
            'Frame Rate (Used for CSS animations)',
            'A Fraction of the available space in the grid container',
            'Font Relative (Similar to rem)'
        ],
        answer: 'A Fraction of the available space in the grid container',
        explanation: 'The `fr` unit mathematically calculates the remaining free space in the grid and distributes it, perfectly accounting for gaps.',
        link: '/css-grid',
        category: 'Advanced Layouts (CSS Grid)',
        difficulty: 'medium',
    },
    {
        question: 'What is the purpose of the `minmax()` function in `grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));`?',
        options: [
            'It hides the element if the screen is smaller than 300px',
            'It forces all columns to be exactly 300px wide',
            'It ensures a column will never be smaller than 300px, but allows it to grow (1fr) if extra space is available',
            'It applies a margin of 300px on mobile devices'
        ],
        answer: 'It ensures a column will never be smaller than 300px, but allows it to grow (1fr) if extra space is available',
        explanation: 'This is the RAM pattern. It creates a fully responsive layout that wraps automatically without requiring media queries.',
        link: '/css-grid',
        category: 'Advanced Layouts (CSS Grid)',
        difficulty: 'hard',
    },

    // ── SCSS Preprocessor ─────────────────────────────────────────────
    {
        question: 'How do you declare a variable in SCSS?',
        options: ['var primaryColor = red;', '@color: red;', '$primary-color: red;', '#primaryColor: red;'],
        answer: '$primary-color: red;',
        explanation: 'SCSS variables always begin with the `$` symbol.',
        link: '/css-scss-preprocessor',
        category: 'SCSS Preprocessor',
        difficulty: 'easy',
    },
    {
        question: 'What does the `&` symbol do when nesting in SCSS?',
        options: [
            'It creates a new CSS animation',
            'It refers to the parent selector',
            'It imports an external font',
            'It concatenates two strings'
        ],
        answer: 'It refers to the parent selector',
        explanation: 'Inside `.btn { &:hover { ... } }`, the `&` is replaced by `.btn`, compiling perfectly to `.btn:hover`.',
        link: '/css-scss-preprocessor',
        category: 'SCSS Preprocessor',
        difficulty: 'medium',
    },
    {
        question: 'Which SCSS feature allows you to define reusable chunks of CSS logic that can accept arguments?',
        options: ['@import', '@extend', '@mixin', '@function'],
        answer: '@mixin',
        explanation: 'A `@mixin` is like a function for styles. You define it once and `@include` it anywhere, passing different arguments if needed.',
        link: '/css-scss-preprocessor',
        category: 'SCSS Preprocessor',
        difficulty: 'medium',
    },

    // ── Animations & Transitions ─────────────────────────────────────
    {
        question: 'Where should you apply the `transition` property to ensure a smooth animation in both directions (e.g., hovering and un-hovering)?',
        options: [
            'On the `:hover` pseudo-class',
            'On the base element itself',
            'Inside an `@keyframes` block',
            'On the parent container'
        ],
        answer: 'On the base element itself',
        explanation: 'If placed on the `:hover` state, the transition only applies while hovering, causing the element to snap back instantly when the mouse leaves.',
        link: '/css-animations',
        category: 'Animations & Transitions',
        difficulty: 'easy',
    },
    {
        question: 'How do you bind a defined `@keyframes` sequence to a specific HTML element?',
        options: [
            'Using the `bind-animation` property',
            'Using the `transition` property',
            'Using the `animation` or `animation-name` property',
            'By wrapping the element in an `<animate>` tag'
        ],
        answer: 'Using the `animation` or `animation-name` property',
        explanation: 'Once you define `@keyframes pulse { ... }`, you apply it using `animation: pulse 2s infinite;`.',
        link: '/css-animations',
        category: 'Animations & Transitions',
        difficulty: 'medium',
    },
    {
        question: 'For maximum rendering performance (60fps), which two CSS properties are the safest to animate?',
        options: [
            'width and height',
            'margin and padding',
            'top and left',
            'transform and opacity'
        ],
        answer: 'transform and opacity',
        explanation: 'Animating layouts (width, margins) forces the browser to recalculate the document geometry. `transform` and `opacity` are hardware-accelerated by the GPU.',
        link: '/css-animations',
        category: 'Animations & Transitions',
        difficulty: 'hard',
    },

    // ── Responsive Design & BEM ─────────────────────────────────────────
    {
        question: 'What is the recommended "Mobile-First" approach when writing CSS media queries?',
        options: [
            'Write desktop styles first, use `@media (max-width)` for mobile',
            'Write mobile styles first, use `@media (min-width)` for larger screens',
            'Use JavaScript to detect the device type instead of CSS',
            'Write separate CSS files for every possible screen size'
        ],
        answer: 'Write mobile styles first, use `@media (min-width)` for larger screens',
        explanation: 'Mobile-first means the default CSS applies to small screens. You use `min-width` queries to add layout complexity only when there is enough screen real estate.',
        link: '/css-responsive-architecture',
        category: 'Responsive Architecture (BEM)',
        difficulty: 'medium',
    },
    {
        question: 'In the BEM methodology (`block__element--modifier`), what does the `--modifier` represent?',
        options: [
            'A completely new component',
            'A change in appearance, state, or behavior (e.g., --active, --dark)',
            'A child element embedded deeply inside the block',
            'A media query breakpoint'
        ],
        answer: 'A change in appearance, state, or behavior (e.g., --active, --dark)',
        explanation: 'Modifiers flag a variation of a block or element without rewriting the entire base component (e.g., `btn btn--primary`).',
        link: '/css-responsive-architecture',
        category: 'Responsive Architecture (BEM)',
        difficulty: 'easy',
    },
    {
        question: 'Which modern CSS function allows a value (like font-size) to scale dynamically between a minimum and maximum bound?',
        options: ['calc()', 'scale()', 'clamp()', 'minmax()'],
        answer: 'clamp()',
        explanation: '`font-size: clamp(1rem, 2.5vw, 2rem);` ensures the text is never smaller than 1rem, never larger than 2rem, but scales fluidly in between based on the viewport width.',
        link: '/css-responsive-architecture',
        category: 'Responsive Architecture (BEM)',
        difficulty: 'medium',
    },
];

// ============================================================
//  UTILITY FUNCTIONS
// ============================================================

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const getStars = (score, total) => {
    const ratio = score / total;
    if (ratio >= QUIZ_CONFIG.threeStarMark) return 3;
    if (ratio >= QUIZ_CONFIG.twoStarMark)   return 2;
    if (ratio >= QUIZ_CONFIG.passMark)       return 1;
    return 0;
};

const getBadge = (stars) => {
    if (stars === 3) return '⚙️ CSS Wizard';
    if (stars === 2) return '🔧 CSS Pro';
    if (stars === 1) return '📘 CSS Apprentice';
    return '🌱 CSS Novice';
};

const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const saveScore = (entry) => {
    const prev    = JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');
    const updated = [entry, ...prev].slice(0, 5);
    localStorage.setItem(QUIZ_CONFIG.leaderboardKey, JSON.stringify(updated));
};

const loadScores = () =>
    JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');

const reorderByDifficulty = (questions, categoryScores) => {
    return [...questions].sort((a, b) => {
        const aRatio = categoryScores[a.category]
            ? categoryScores[a.category].correct / categoryScores[a.category].total
            : 0.5;
        const bRatio = categoryScores[b.category]
            ? categoryScores[b.category].correct / categoryScores[b.category].total
            : 0.5;
        return bRatio - aRatio;
    });
};

// ============================================================
//  SOUND ENGINE — Web Audio API
// ============================================================

let audioCtx = null;

const getAudioContext = () => {
    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || window['webkitAudioContext'];
        audioCtx = new AudioContextClass();
    }
    return audioCtx;
};

const playTone = (frequency, duration, type = 'sine', volume = 0.3, delay = 0) => {
    try {
        const ctx  = getAudioContext();
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = type;
        osc.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + duration);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + duration + 0.05);
    } catch (e) {
        console.warn('Sound playback failed:', e);
    }
};

const playCorrect = () => {
    playTone(523, 0.15, 'sine', 0.3, 0);
    playTone(659, 0.2,  'sine', 0.3, 0.12);
};

const playWrong = () => {
    playTone(300, 0.1,  'square', 0.2, 0);
    playTone(200, 0.15, 'square', 0.2, 0.12);
};

const playWarningBeep = () => playTone(880, 0.08, 'sine', 0.25, 0);

const playComplete = () =>
    [523, 659, 784, 1047].forEach((f, i) => playTone(f, 0.25, 'sine', 0.35, i * 0.12));

const playGoldenBuzzer = () => {
    [523, 659, 784].forEach((f) => playTone(f, 0.6, 'sine', 0.25, 0));
    [523, 659, 784, 1047, 1319].forEach((f, i) =>
        playTone(f, 0.3, 'triangle', 0.3, 0.5 + i * 0.1)
    );
};

const speakText = (text, rate = 0.9) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter  = new SpeechSynthesisUtterance(text);
    utter.rate   = rate;
    utter.pitch  = 1.1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
};

// ============================================================
//  STYLED COMPONENTS
// ============================================================

const pulseGold = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(229,201,95,0.4); }
    50%       { box-shadow: 0 0 0 20px rgba(229,201,95,0); }
`;

const shakeAnim = keyframes`
    0%, 100% { transform: translateX(0); }
    20%       { transform: translateX(-8px); }
    40%       { transform: translateX(8px); }
    60%       { transform: translateX(-6px); }
    80%       { transform: translateX(6px); }
`;

const PageWrapper = styled(Box)({
    minHeight:       '100vh',
    backgroundColor: BRAND.dark,
    fontFamily:      "'Poppins', sans-serif",
    padding:         '24px 16px 48px',
    overflowX:       'hidden',
});

const QuizCard = styled(Card)({
    backgroundColor: BRAND.darkCard,
    border:          `1px solid ${BRAND.darkBorder}`,
    borderRadius:    '20px',
    backdropFilter:  'blur(10px)',
    maxWidth:        '860px',
    margin:          '0 auto',
    overflow:        'visible',
});

const GoldButton = styled(Button)({
    background:    `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color:         BRAND.dark,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    800,
    fontSize:      '0.9rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding:       '12px 28px',
    borderRadius:  '10px',
    transition:    'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform:  'translateY(-2px)',
        boxShadow:  '0 8px 20px rgba(229,201,95,0.3)',
    },
    '&.Mui-disabled': {
        background: 'rgba(255,255,255,0.1)',
        color:      'rgba(255,255,255,0.3)',
    },
});

const OutlineButton = styled(Button)({
    borderColor:   BRAND.gold,
    color:         BRAND.gold,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    700,
    borderRadius:  '10px',
    padding:       '10px 24px',
    transition:    'background 0.2s',
    '&:hover': {
        backgroundColor: BRAND.goldFaint,
        borderColor:     BRAND.goldenrod,
    },
});

const ViolationBanner = styled(Box)({
    backgroundColor: 'rgba(255,107,107,0.15)',
    border:          '1px solid rgba(255,107,107,0.4)',
    borderRadius:    '10px',
    padding:         '10px 16px',
    display:         'flex',
    alignItems:      'center',
    gap:             '10px',
    marginBottom:    '16px',
    animation:       `${shakeAnim} 0.4s ease`,
});

// ============================================================
//  STAR DISPLAY COMPONENT
// ============================================================
const StarDisplay = ({ stars, size = 'large' }) => {
    const fontSize = size === 'large' ? '2.5rem' : '1.2rem';
    return (
        <Box display="flex" gap={0.5} justifyContent="center">
            {[1, 2, 3].map((i) =>
                i <= stars
                    ? <StarIcon key={i} sx={{ fontSize, color: BRAND.gold, filter: 'drop-shadow(0 0 4px rgba(229,201,95,0.6))' }} />
                    : <StarBorderIcon key={i} sx={{ fontSize, color: 'rgba(255,255,255,0.2)' }} />
            )}
        </Box>
    );
};

// ============================================================
//  MAIN COMPONENT — CssQuiz
// ============================================================
function CssQuiz() {

    const [phase,      setPhase]     = useState('welcome');
    const [userName,   setUserName]  = useState('');
    const [nameInput,  setNameInput] = useState('');

    const [questions,   setQuestions]   = useState([]);
    const [current,     setCurrent]     = useState(0);
    const [score,       setScore]       = useState(0);
    const [selected,    setSelected]    = useState(null);
    const [showAnswer,  setShowAnswer]  = useState(false);
    const [userAnswers, setUserAnswers] = useState([]);
    const [isRetry,     setIsRetry]     = useState(false);

    const [prevCategoryScores, setPrevCategoryScores] = useState({});

    const [timer,    setTimer]    = useState(QUIZ_CONFIG.totalTimeSeconds);
    const [finished, setFinished] = useState(false);

    const [violations,    setViolations]    = useState(0);
    const [violationLog,  setViolationLog]  = useState([]);
    const [showViolation, setShowViolation] = useState(false);
    const [lastViolation, setLastViolation] = useState('');

    const violationCountRef = useRef(0);
    const quizActiveRef     = useRef(false);

    // ============================================================
    //  QUESTION INITIALISATION
    // ============================================================
    const initQuestions = useCallback((retry, catScores) => {
        let bank = cssQuestionBank.map(q => ({ ...q, options: shuffle(q.options) }));
        if (retry && Object.keys(catScores).length > 0) {
            bank = reorderByDifficulty(bank, catScores);
        } else {
            bank = shuffle(bank);
        }
        setQuestions(bank);
    }, []);

    // ============================================================
    //  ANTI-CHEAT ENGINE
    // ============================================================
    const recordViolation = useCallback((message) => {
        if (!quizActiveRef.current) return;
        violationCountRef.current += 1;
        const count = violationCountRef.current;
        setViolations(count);
        setViolationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
        setLastViolation(message);
        setShowViolation(true);
        playTone(440, 0.3, 'square', 0.3, 0);
        speakText('Violation detected. ' + message);
        setTimeout(() => setShowViolation(false), 4000);
        if (count >= QUIZ_CONFIG.maxViolations) {
            speakText('Maximum violations reached. Quiz terminated.');
            quizActiveRef.current = false;
            setFinished(true);
        }
    }, []);

    useEffect(() => {
        if (phase !== 'quiz') return;

        const handleVisibility  = () => { if (document.hidden) recordViolation('Tab switch or window minimised detected.'); };
        const handleBlur        = ()  => recordViolation('Browser window lost focus.');
        const handleContextMenu = (e) => { e.preventDefault(); recordViolation('Right-click attempt blocked.'); };
        const handleKeyDown     = (e) => {
            const blocked =
                (e.ctrlKey && ['c', 'v', 'u', 's', 'a', 'p'].includes(e.key.toLowerCase())) ||
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()));
            if (blocked) {
                e.preventDefault();
                recordViolation(`Blocked shortcut: ${e.ctrlKey ? 'Ctrl+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`);
            }
        };
        const handleResize = () => {
            if (window.outerHeight - window.innerHeight > 160 || window.outerWidth - window.innerWidth > 160) {
                recordViolation('Possible DevTools opened — browser size change detected.');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('blur',          handleBlur);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown',     handleKeyDown);
        window.addEventListener('resize',        handleResize);
        quizActiveRef.current = true;

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('blur',          handleBlur);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown',     handleKeyDown);
            window.removeEventListener('resize',        handleResize);
            quizActiveRef.current = false;
        };
    }, [phase, recordViolation]);

    // ============================================================
    //  COUNTDOWN TIMER
    // ============================================================
    useEffect(() => {
        if (phase !== 'quiz' || finished) return;
        if (timer <= 0) { speakText('Time is up. Quiz finished.'); setFinished(true); return; }
        if (timer <= QUIZ_CONFIG.warningThreshold && timer % 10 === 0) {
            playWarningBeep();
            if (timer === QUIZ_CONFIG.warningThreshold) speakText(`${QUIZ_CONFIG.warningThreshold} seconds remaining.`);
        }
        const id = setTimeout(() => setTimer(t => t - 1), 1000);
        return () => clearTimeout(id);
    }, [timer, finished, phase]);

    // ============================================================
    //  RESULTS TRIGGER
    // ============================================================
    useEffect(() => {
        if (!finished || questions.length === 0) return;
        quizActiveRef.current = false;
        const stars = getStars(score, questions.length);

        if (stars === 3) {
            playGoldenBuzzer();
            speakText(`Congratulations ${userName}! Perfect score! Golden Buzzer and three stars!`);
        } else {
            playComplete();
            speakText(`Well done ${userName}. You scored ${score} out of ${questions.length}. ${stars} star${stars !== 1 ? 's' : ''}.`);
        }

        const catStats = {};
        questions.forEach((q, i) => {
            if (!catStats[q.category]) catStats[q.category] = { correct: 0, total: 0 };
            catStats[q.category].total++;
            if (userAnswers[i] === q.answer) catStats[q.category].correct++;
        });
        setPrevCategoryScores(catStats);
        saveScore({ date: new Date().toLocaleString(), score, total: questions.length, timeRemaining: timer, badge: getBadge(stars), stars, name: userName });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finished]);

    // ============================================================
    //  EVENT HANDLERS
    // ============================================================
    const handleNameSubmit = () => {
        const trimmed = nameInput.trim();
        if (!trimmed) return;
        setUserName(trimmed);
        speakText(`Welcome ${trimmed}. Please read the rules carefully before you begin.`);
        setPhase('rules');
    };

    const handleStartQuiz = () => {
        initQuestions(isRetry, prevCategoryScores);
        setCurrent(0); setScore(0); setSelected(null);
        setShowAnswer(false); setUserAnswers([]);
        setTimer(QUIZ_CONFIG.totalTimeSeconds); setFinished(false);
        setViolations(0); setViolationLog([]);
        violationCountRef.current = 0;
        setPhase('quiz');

        speakText(`Are you ready, ${userName}?`, 0.85);
        setTimeout(() => speakText('3', 1.2), 1800);
        setTimeout(() => speakText('2', 1.2), 2800);
        setTimeout(() => speakText('1', 1.2), 3800);
        setTimeout(() => speakText('Begin!', 1.0), 4800);
    };

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setUserAnswers(prev => [...prev, opt]);
        setShowAnswer(true);
        if (opt === questions[current].answer) { setScore(s => s + 1); playCorrect(); }
        else { playWrong(); }
    };

    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1); setShowAnswer(false); setSelected(null);
        } else { setFinished(true); }
    };

    const handleRetry = () => {
        setIsRetry(true);
        setPhase('rules');
        speakText(`Let's try again, ${userName}. Questions are ordered by your weakest areas.`);
    };

    // ============================================================
    //  DERIVED VALUES
    // ============================================================
    const categoryStats = questions.reduce((acc, q, i) => {
        if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
        acc[q.category].total++;
        if (finished && userAnswers[i] === q.answer) acc[q.category].correct++;
        return acc;
    }, {});

    const stars          = finished ? getStars(score, questions.length) : 0;
    const passed         = stars >= 1;
    const isGoldenBuzzer = stars === 3;
    const progressPct    = questions.length > 0 ? (current / questions.length) * 100 : 0;
    const timerWarning   = timer <= QUIZ_CONFIG.warningThreshold;

    // ============================================================
    //  RENDER — WELCOME SCREEN
    // ============================================================
    if (phase === 'welcome') {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={4}>
                            <Typography sx={{ fontSize: '3rem', lineHeight: 1 }}>🎨</Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: BRAND.white, letterSpacing: '-0.02em', mt: 1 }}>
                                CSS & UI Mastery Quiz
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                ProDev Adaptive Learning Platform
                            </Typography>
                            <Box mt={2}><StarDisplay stars={3} size="large" /></Box>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 4 }} />

                        <Box maxWidth={420} mx="auto">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '1rem', color: BRAND.white, mb: 0.5 }}>
                                Enter your full name to begin
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                Your name will appear on your certificate and leaderboard entry.
                            </Typography>

                            <Box
                                component="input"
                                placeholder="e.g. Kofi Boateng"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                                sx={{
                                    width: '100%', p: '14px 18px', borderRadius: '10px',
                                    border: `1px solid ${BRAND.darkBorder}`,
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    color: BRAND.white, fontFamily: "'Poppins', sans-serif",
                                    fontSize: '1rem', outline: 'none', mb: 3, boxSizing: 'border-box',
                                    '&:focus':        { borderColor: BRAND.gold },
                                    '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                                }}
                            />

                            <GoldButton fullWidth onClick={handleNameSubmit} disabled={!nameInput.trim()} endIcon={<ArrowForwardIcon />}>
                                Continue to Rules
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RULES SCREEN
    // ============================================================
    if (phase === 'rules') {
        const rules = [
            { icon: <TimerIcon />,               title: 'Time Limit',         text: `You have ${QUIZ_CONFIG.totalTimeSeconds / 60} minutes to answer all ${cssQuestionBank.length} questions. Timer starts immediately when you click Begin.` },
            { icon: <StarIcon />,                 title: 'Star Rewards',       text: 'Score 80%+ for ★, 90%+ for ★★, 100% for ★★★ (Golden Buzzer!). Earn at least ★ to unlock the Final Certification Exam.' },
            { icon: <ReplayIcon />,               title: 'Failed Attempts',    text: 'Score below 80% and you must retake. Questions reorder based on your weakest categories — easiest first.' },
            { icon: <SecurityIcon />,             title: 'Anti-Cheat Rules',   text: `No tab switching, minimising, DevTools, right-clicking, or Ctrl+C/V. ${QUIZ_CONFIG.maxViolations} violations auto-terminate your quiz.` },
            { icon: <NotificationsActiveIcon />,  title: 'Audio Enabled',      text: 'Audio feedback and speech synthesis are active. Keep your volume on. A countdown will play before the first question.' },
            { icon: <SchoolIcon />,               title: 'Academic Integrity', text: 'Do not search for answers online. This quiz tests YOUR CSS knowledge. Your certificate only means something if you earn it honestly.' },
        ];

        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.gold }}>
                                Welcome, {userName}! 👋
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem' }}>
                                {isRetry ? 'Retaking with adaptive difficulty — weak categories come last.' : 'Read the rules carefully before you begin.'}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 3 }} />

                        {rules.map((rule, i) => (
                            <Box key={i} display="flex" gap={2} alignItems="flex-start" mb={2}
                                 sx={{ p: 2, backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '12px' }}
                            >
                                <Box sx={{ color: BRAND.gold, mt: 0.2, flexShrink: 0 }}>{rule.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: BRAND.white, mb: 0.3 }}>
                                        {rule.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6 }}>
                                        {rule.text}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ borderColor: BRAND.darkBorder, my: 3 }} />

                        <Box textAlign="center">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: BRAND.white, mb: 3 }}>
                                Are you ready, {userName}? A countdown will play before the first question.
                            </Typography>
                            <GoldButton onClick={handleStartQuiz} startIcon={<PlayArrowIcon />}
                                        sx={{ fontSize: '1rem', px: 5, py: 1.5, animation: `${pulseGold} 2s ease-in-out infinite` }}
                            >
                                I'm Ready — Begin!
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RESULTS SCREEN
    // ============================================================
    if (finished) {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                        {isGoldenBuzzer && (
                            <Box textAlign="center" sx={{
                                background: 'linear-gradient(135deg, rgba(229,201,95,0.2), rgba(218,165,32,0.1))',
                                border: `2px solid ${BRAND.gold}`, borderRadius: '16px',
                                p: 3, mb: 3, animation: `${pulseGold} 1.5s ease-in-out infinite`,
                            }}>
                                <Typography sx={{ fontSize: '2.5rem' }}>🔔</Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1.5rem', color: BRAND.gold }}>
                                    GOLDEN BUZZER!
                                </Typography>
                                <Typography sx={{ color: BRAND.dimWhite, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                    Perfect score! You are a CSS master, {userName}!
                                </Typography>
                            </Box>
                        )}

                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: BRAND.white, mb: 1 }}>
                                {passed ? '🎉 Well Done,' : '😓 Keep Going,'} {userName}!
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', color: BRAND.dimWhite, mb: 2 }}>
                                Score: <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>{score}</Box> / {questions.length} ({Math.round((score / questions.length) * 100)}%)
                            </Typography>
                            <StarDisplay stars={stars} size="large" />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1rem', color: BRAND.gold, mt: 1.5, fontWeight: 600 }}>
                                {getBadge(stars)}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mt: 0.5 }}>
                                ⏱ Time remaining: {formatTime(timer)}
                            </Typography>
                        </Box>

                        {passed ? (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(62,207,142,0.08)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <EmojiEventsIcon sx={{ color: BRAND.success, fontSize: 32, mb: 0.5 }} />
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.success, fontSize: '0.95rem', mb: 2 }}>
                                    You are eligible for the CSS Certification Exam!
                                </Typography>
                                <GoldButton component={Link} to="/css-certification-exam" endIcon={<ArrowForwardIcon />}>
                                    Attempt Certification Exam
                                </GoldButton>
                            </Box>
                        ) : (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.95rem', mb: 0.5 }}>
                                    You need 80%+ to unlock the CSS Certification Exam.
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                    Retake reorders questions — easier topics first based on your weak areas.
                                </Typography>
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake Quiz (Adaptive)
                                </OutlineButton>
                            </Box>
                        )}

                        {violations > 0 && (
                            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '12px' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.88rem', mb: 1 }}>
                                    ⚠ {violations} Violation{violations > 1 ? 's' : ''} Detected
                                </Typography>
                                {violationLog.map((v, i) => (
                                    <Typography key={i} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.7)', lineHeight: 1.7 }}>
                                        {v}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            Category Breakdown
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {Object.entries(categoryStats).map(([cat, data]) => {
                                const pct      = Math.round((data.correct / data.total) * 100);
                                const barColor = pct >= 80 ? BRAND.success : pct >= 50 ? BRAND.warning : BRAND.error;
                                return (
                                    <Box key={cat} sx={{ p: '10px 16px', backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '10px' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.82rem', color: BRAND.white }}>{cat}</Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: barColor, fontWeight: 700 }}>
                                                {data.correct}/{data.total} ({pct}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={pct} sx={{
                                            height: 4, borderRadius: 2,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                            '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 2 },
                                        }} />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            🏅 Leaderboard — Top 5
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {loadScores().map((entry, idx) => (
                                <Box key={idx} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    p: '10px 16px',
                                    backgroundColor: idx === 0 ? 'rgba(229,201,95,0.1)' : BRAND.goldFaint,
                                    border: `1px solid ${idx === 0 ? BRAND.gold : BRAND.darkBorder}`,
                                    borderRadius: '10px',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: idx === 0 ? BRAND.gold : BRAND.white }}>
                                            #{idx + 1} {entry.name}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.date} • ⏱ {formatTime(entry.timeRemaining)} left
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <StarDisplay stars={entry.stars} size="small" />
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.score}/{entry.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <OutlineButton component={Link} to="/css-dashboard" variant="outlined">Back to Dashboard</OutlineButton>
                            {passed && (
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake for Better Score
                                </OutlineButton>
                            )}
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — ACTIVE QUIZ SCREEN
    // ============================================================
    if (phase === 'quiz' && questions.length > 0) {
        const q = questions[current];

        return (
            <PageWrapper>
                {/* Sticky header */}
                <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: BRAND.dark, borderBottom: `1px solid ${BRAND.darkBorder}`, py: 1.5, px: 2, mb: 2 }}>
                    <Box maxWidth="860px" mx="auto" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: BRAND.dimWhite }}>
                            Question <Box component="span" sx={{ color: BRAND.gold }}>{current + 1}</Box> of {questions.length}
                        </Typography>
                        <Chip label={q.category} size="small" sx={{ backgroundColor: BRAND.goldFaint, color: BRAND.gold, border: `1px solid ${BRAND.darkBorder}`, fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.7rem' }} />
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TimerIcon sx={{ color: timerWarning ? BRAND.error : BRAND.gold, fontSize: '1.1rem' }} />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1rem', color: timerWarning ? BRAND.error : BRAND.gold, animation: timerWarning ? `${pulseGold} 1s ease-in-out infinite` : 'none' }}>
                                {formatTime(timer)}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.dimWhite }}>
                            Score: <Box component="span" sx={{ color: BRAND.gold }}>{score}</Box>
                        </Typography>
                    </Box>
                    <Box maxWidth="860px" mx="auto" mt={1}>
                        <LinearProgress variant="determinate" value={progressPct} sx={{
                            height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.06)',
                            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`, borderRadius: 2 },
                        }} />
                    </Box>
                </Box>

                {/* Violation banner */}
                {showViolation && (
                    <Box maxWidth="860px" mx="auto" px={2} mb={1}>
                        <ViolationBanner>
                            <WarningAmberIcon sx={{ color: BRAND.error, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.error }}>
                                    ⚠ Violation #{violations} Detected
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.8)' }}>
                                    {lastViolation} — {QUIZ_CONFIG.maxViolations - violations} more will terminate this quiz.
                                </Typography>
                            </Box>
                        </ViolationBanner>
                    </Box>
                )}

                {/* Question card */}
                <QuizCard>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <Typography sx={{
                            fontFamily: "'Poppins', sans-serif", fontWeight: 700,
                            fontSize: { xs: '1rem', md: '1.15rem' }, color: BRAND.white,
                            mb: 3, lineHeight: 1.6, p: 2.5,
                            backgroundColor: BRAND.goldFaint,
                            borderLeft: `3px solid ${BRAND.gold}`,
                            borderRadius: '0 10px 10px 0',
                        }}>
                            {current + 1}. {q.question}
                        </Typography>

                        <Box>
                            {q.options.map((opt, i) => {
                                const isCorrect = showAnswer && opt === q.answer;
                                const isWrong   = showAnswer && opt === selected && opt !== q.answer;
                                return (
                                    <Button key={i} onClick={() => handleAnswer(opt)} disabled={showAnswer}
                                            startIcon={
                                                isCorrect ? <CheckCircleIcon sx={{ color: BRAND.success }} /> :
                                                    isWrong   ? <CancelIcon      sx={{ color: BRAND.error   }} /> : null
                                            }
                                            sx={{
                                                width: '100%', textAlign: 'left', justifyContent: 'flex-start',
                                                p: '14px 20px', borderRadius: '10px',
                                                fontFamily: "'Poppins', sans-serif", fontWeight: 500, fontSize: '0.9rem',
                                                textTransform: 'none', mb: 1,
                                                backgroundColor: isCorrect ? 'rgba(62,207,142,0.15)' : isWrong ? 'rgba(255,107,107,0.15)' : 'rgba(255,255,255,0.04)',
                                                border: `1px solid ${isCorrect ? BRAND.success : isWrong ? BRAND.error : 'rgba(255,255,255,0.12)'}`,
                                                color: isCorrect ? BRAND.success : isWrong ? BRAND.error : BRAND.white,
                                                transition: 'all 0.25s ease',
                                                animation: isWrong ? `${shakeAnim} 0.4s ease` : 'none',
                                                '&:hover': !showAnswer ? { backgroundColor: 'rgba(229,201,95,0.08)', borderColor: BRAND.gold } : {},
                                                '&.Mui-disabled': { opacity: 1 },
                                            }}
                                    >
                                        {opt}
                                    </Button>
                                );
                            })}
                        </Box>

                        {showAnswer && (
                            <Box sx={{
                                mt: 2, p: 2,
                                backgroundColor: selected === q.answer ? 'rgba(62,207,142,0.06)' : 'rgba(255,107,107,0.06)',
                                border: `1px solid ${selected === q.answer ? 'rgba(62,207,142,0.3)' : 'rgba(255,107,107,0.3)'}`,
                                borderRadius: '10px',
                            }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: selected === q.answer ? BRAND.success : BRAND.error, mb: 0.5 }}>
                                    {selected === q.answer ? '✅ Correct!' : `❌ Incorrect — Correct answer: ${q.answer}`}
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6, fontStyle: 'italic' }}>
                                    {q.explanation}
                                </Typography>
                                <Typography component={Link} to={q.link} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: BRAND.gold, mt: 0.5, display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    📖 Review this topic →
                                </Typography>
                            </Box>
                        )}

                        {showAnswer && (
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <GoldButton onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                                    {current + 1 < questions.length ? 'Next Question' : 'Finish Quiz'}
                                </GoldButton>
                            </Box>
                        )}
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <Typography sx={{ color: BRAND.white, textAlign: 'center', pt: 10, fontFamily: "'Poppins', sans-serif" }}>
                Loading quiz...
            </Typography>
        </PageWrapper>
    );
}

export default CssQuiz;
